import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { Building2, Inbox, Sparkles, X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useLocale } from '../../hooks/useLocale';
import {
  hasSeenEmployerWelcome,
  markEmployerWelcomeSeen,
} from '../../lib/employer-welcome';
import { trackProductEvent } from '../../lib/product-analytics';

const FEATURES = [
  { key: 'companyPage', icon: Building2 },
  { key: 'applications', icon: Inbox },
  { key: 'agent', icon: Sparkles },
] as const;

// Small settle delay so the employer shell paints before the dialog fades in.
const SHOW_DELAY_MS = 450;

/**
 * First-entry walkthrough for the employer workspace, mirroring the seeker
 * PostOnboardingWelcomeContent design. Mounted only inside (employer)'s
 * company-workspace branch, so the workspace checks are implicit. Shows once
 * per user (lib/employer-welcome.ts).
 */
export function EmployerWelcomeGate() {
  const { t } = useTranslation('employer');
  const { isRTL } = useLocale();
  const router = useRouter();
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user?.id || hasSeenEmployerWelcome(user.id)) return undefined;
    const timer = window.setTimeout(() => {
      setVisible(true);
      trackProductEvent('employer_welcome_shown', {});
    }, SHOW_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [user?.id]);

  const dismiss = () => {
    markEmployerWelcomeSeen(user?.id);
    setVisible(false);
  };

  const openCompanyPage = () => {
    trackProductEvent('employer_welcome_open_company_page', {});
    dismiss();
    router.push('/(employer)/company' as never);
  };

  const userId = user?.id;
  useEffect(() => {
    if (!visible) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        markEmployerWelcomeSeen(userId);
        setVisible(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [visible, userId]);

  if (!visible || typeof document === 'undefined') return null;

  const dialog = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="employer-welcome-title"
      className="fixed inset-0 flex items-center justify-center"
      style={{
        direction: isRTL ? 'rtl' : 'ltr',
        padding: 18,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        zIndex: 9999,
      }}
    >
      <button
        type="button"
        aria-label={t('welcome.close', { defaultValue: 'Close' })}
        onClick={dismiss}
        className="absolute inset-0 h-full w-full cursor-default border-0 bg-transparent p-0"
      />

      <section
        className="post-onboarding-welcome-dialog relative flex w-full flex-col overflow-hidden"
        style={{
          maxWidth: 520,
          maxHeight: 'calc(100vh - 36px)',
          borderRadius: 28,
          backgroundColor: 'var(--tabbio-bg)',
          color: 'var(--tabbio-text)',
          boxShadow: '0 24px 70px rgba(0, 0, 0, 0.22)',
        }}
      >
        <button
          type="button"
          aria-label={t('welcome.close', { defaultValue: 'Close' })}
          onClick={dismiss}
          className="absolute top-4 flex h-9 w-9 items-center justify-center border-0 bg-transparent"
          style={{
            insetInlineEnd: 18,
            borderRadius: 12,
            color: 'var(--tabbio-text-tertiary)',
            cursor: 'pointer',
          }}
          onMouseEnter={(event) => {
            event.currentTarget.style.backgroundColor =
              'var(--tabbio-surface-secondary)';
            event.currentTarget.style.color = 'var(--tabbio-text)';
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.backgroundColor = 'transparent';
            event.currentTarget.style.color = 'var(--tabbio-text-tertiary)';
          }}
        >
          <X size={18} strokeWidth={2.2} />
        </button>

        <div
          className="post-onboarding-welcome-scroll flex-1 overflow-y-auto"
          style={{ padding: '46px 42px 22px' }}
        >
          <h1
            id="employer-welcome-title"
            style={{
              margin: 0,
              color: 'var(--tabbio-text)',
              fontSize: 36,
              lineHeight: '42px',
              fontWeight: 680,
              letterSpacing: 0,
              maxWidth: 400,
            }}
          >
            {t('welcome.title', {
              defaultValue: 'Welcome to your company workspace',
            })}
          </h1>
          <p
            style={{
              margin: '18px 0 0',
              color: 'var(--tabbio-text-secondary)',
              fontSize: 17,
              lineHeight: '27px',
              maxWidth: 430,
            }}
          >
            {t('welcome.subtitle', {
              defaultValue:
                'Everything you need to hire on Tabbio lives here.',
            })}
          </p>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 26,
              marginTop: 34,
            }}
          >
            {FEATURES.map(({ key, icon: Icon }) => (
              <div
                key={key}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 20,
                  flexDirection: isRTL ? 'row-reverse' : 'row',
                }}
              >
                <div
                  style={{
                    width: 42,
                    minHeight: 42,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    paddingTop: 2,
                    flexShrink: 0,
                  }}
                >
                  <Icon size={28} color="var(--tabbio-text)" strokeWidth={2.3} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h2
                    style={{
                      margin: 0,
                      color: 'var(--tabbio-text)',
                      fontSize: 18,
                      lineHeight: '24px',
                      fontWeight: 680,
                      letterSpacing: 0,
                    }}
                  >
                    {t(`welcome.features.${key}.title`)}
                  </h2>
                  <p
                    style={{
                      margin: '4px 0 0',
                      color: 'var(--tabbio-text-secondary)',
                      fontSize: 16,
                      lineHeight: '25px',
                    }}
                  >
                    {t(`welcome.features.${key}.body`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          style={{
            padding: '10px 42px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <button
            type="button"
            onClick={openCompanyPage}
            style={{
              width: '100%',
              minHeight: 56,
              borderRadius: 999,
              border: 0,
              backgroundColor: 'var(--tabbio-text)',
              color: 'var(--tabbio-bg)',
              font: 'inherit',
              fontSize: 17,
              fontWeight: 680,
              cursor: 'pointer',
            }}
          >
            {t('welcome.primaryCta', { defaultValue: 'Open Company Page' })}
          </button>
          <button
            type="button"
            onClick={dismiss}
            style={{
              width: '100%',
              minHeight: 38,
              border: 0,
              background: 'transparent',
              color: 'var(--tabbio-text-secondary)',
              font: 'inherit',
              fontSize: 14,
              fontWeight: 550,
              cursor: 'pointer',
            }}
          >
            {t('welcome.skipCta', { defaultValue: 'Not now' })}
          </button>
        </div>
      </section>
    </div>
  );

  return createPortal(dialog, document.body);
}
